import type WaCheckbox from "@web.awesome.me/webawesome-pro/dist/components/checkbox/checkbox.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import { css, html, LitElement, type PropertyValues } from "lit";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { attributeProperty } from "../../property";
import type { ParserType } from "../../schemas/parser";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

/**
This item's value type.

An object describing both the item's description and checked state.
*/
export type ChecklistItemValue = {
  isChecked: boolean;
  description: string;
};

type Description = ParserType<typeof Description>;
const Description = attributeProperty("description", z.string(), {
  defaultValue: "",
});

// TODO: to correctly support the form reset, we need a default value like
// Web Awesome components do.
export class WmChecklistItem extends WmElement {
  static readonly TAG_NAME = "wm-checklist-item";

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static styles = [
    hostStyles,
    css`
    :host([checked]) {
      text-decoration: line-through;
    }

    [slot="start"] {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  
    wa-checkbox::part(control) {
      margin: 0;
    }

    wa-input::part(form-control-label) {
      display: none;
    }
  `,
  ];

  static properties = {
    description: Description,
    // TODO: implement property overrides for converter.
    checked: {
      type: Boolean,
      reflect: true,
    },
  };

  declare checked: boolean;
  declare description: Description;

  constructor() {
    super();
    this.checked = false;
  }

  get value(): ChecklistItemValue {
    return {
      isChecked: this.checked,
      description: this.description,
    };
  }

  // Should only be called after render, as it query selects and asserts not null.
  get validationTarget() {
    return checkNotNil(checkNotNil(this.renderRoot.querySelector("wa-input")));
  }

  reportError(message: string) {
    const input = this.#queryInput();
    input.setCustomValidity(message);
    input.reportValidity();
  }

  override render() {
    return html`
      <wa-input size="s" value="${this.description}" @input=${this.#handleDescriptionInput} required>
        <div slot="start">
          <wa-checkbox size="s" @input=${this.#checkedInput} ?checked=${this.checked}></wa-checkbox>
          <wa-button tabindex="-1" slot="start" appearance="plain" size="xs">
            <wa-icon class="handle" name="grip-vertical" size="xs"></wa-icon>
          </wa-button>
        </div>
        <wm-clear-button slot="end" size="s" @click=${this.#removeClicked}>
        </wm-clear-button>
      </wa-input>
      `;
  }

  // TODO: must be smart in event dispatch, as the validation target is only
  // available *after* it finishes updating, if I'm not mistaken. So we need
  // to wait for wa-input to finish its update cycle, only then bubble up the
  // thang.
  protected override updated(props: PropertyValues<this>): void {
    if (props.has("checked") || props.has("description")) {
      this.dispatchEvent(new WmChecklistItemUpdatedEvent());
    }
  }

  override async getUpdateComplete() {
    const result = await super.getUpdateComplete();
    // Await for wa-input to finish updating. This is required to get good
    // form related metadata, such as validity and stuff.
    await this.validationTarget.updateComplete;
    return result;
  }

  #handleDescriptionInput(event: InputEvent) {
    // Do not bubble outside.
    event.stopPropagation();
    const element = checkNotNil(event.target) as WaInput;
    this.description = element.value ?? "";
    element.setCustomValidity("");
  }

  #checkedInput(event: InputEvent) {
    // Do not bubble up to the parent wa-input.
    event.stopPropagation();
    const element = checkNotNil(event.target) as WaCheckbox;
    this.checked = element.checked;
  }

  #removeClicked(source: PointerEvent) {
    const event = new WmChecklistItemRemovedEvent(source);
    this.dispatchEvent(event);
  }

  #queryInput(): WaInput {
    return this.queryNotNil("wa-input");
  }
}

customElements.define(WmChecklistItem.TAG_NAME, WmChecklistItem);

declare global {
  interface HTMLElementTagNameMap {
    [WmChecklistItem.TAG_NAME]: WmChecklistItem;
  }
}

export class WmChecklistItemUpdatedEvent extends Event {
  static readonly EVENT_NAME = `${WmChecklistItem.TAG_NAME}-updated`;

  constructor() {
    super(WmChecklistItemUpdatedEvent.EVENT_NAME, {
      bubbles: true,
      cancelable: false,
      composed: true,
    });
  }
}

export class WmChecklistItemRemovedEvent extends Event {
  static readonly EVENT_NAME = `${WmChecklistItem.TAG_NAME}-removed`;

  readonly source: PointerEvent;

  constructor(source: PointerEvent) {
    super(WmChecklistItemRemovedEvent.EVENT_NAME, {
      bubbles: true,
      cancelable: false,
      composed: true,
    });
    this.source = source;
  }
}
